import React, { useState, useEffect, useRef } from 'react';
import useDarkMode from '../../../hooks/useDarkMode';
import { useSelector } from 'react-redux';
import moment from 'moment';
import useThemeColor from "../../../hooks/useThemeColor";
import './MUIDatePicker.css';

const DebugMUIDatePicker = ({ 
  label, 
  value, 
  onChange, 
  placeholder = "Select date",
  disabled = false,
  clearable = false,
  minDate = null,
  maxDate = null,
  helperText = "",
  error = false,
  name = "",
  required = false,
  className = "",
  style = {},
  ...props 
}) => {
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  const isDarkMode = useDarkMode();
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(moment());
  const [selectedDate, setSelectedDate] = useState(value ? moment(value) : null);
  
  const containerRef = useRef(null);

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  

  // Update selected date when value prop changes
  useEffect(() => {
    setSelectedDate(value ? moment(value) : null);
  }, [value]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleCalendar = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!disabled) {
      console.log('Debug: Toggling calendar from', isOpen, 'to', !isOpen);
      setIsOpen(prev => !prev);
    }
  };

  const handleDateSelect = (date) => {
    console.log('Debug: Date selected:', date.format('MM/DD/YYYY'));
    setSelectedDate(date);
    if (onChange) {
      onChange(date.toDate());
    }
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedDate(null);
    if (onChange) {
      onChange(null);
    }
  };

  const isDateDisabled = (date) => {
    if (minDate && date.isBefore(moment(minDate), 'day')) return true;
    if (maxDate && date.isAfter(moment(maxDate), 'day')) return true;
    return false;
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = moment(prev);
      if (direction === 'prev') {
        newDate.subtract(1, 'month');
      } else {
        newDate.add(1, 'month');
      }
      console.log('Debug: Navigating to', newDate.format('YYYY-MM'));
      return newDate;
    });
  };

  const renderSimpleCalendar = () => {
    // Simple calendar with hardcoded days for testing
    const daysInMonth = currentDate.daysInMonth();
    const firstDayOfMonth = moment(currentDate).startOf('month').day(); // 0 = Sunday
    const today = moment();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = moment(currentDate).date(day);
      days.push(date);
    }

    console.log('Debug: Rendering calendar for', currentDate.format('YYYY-MM'), {
      daysInMonth,
      firstDayOfMonth,
      totalDays: days.length,
      currentDateStr: currentDate.format('YYYY-MM-DD')
    });

    return (
      <div 
        className={`mui-calendar-popup ${isDarkMode ? 'dark' : 'light'}`}
        style={{ 
          '--theme-color': themeColor,
          backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
          border: `1px solid ${isDarkMode ? '#404040' : '#dee2e6'}`,
          borderRadius: '12px',
          padding: '16px',
          minWidth: '320px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '16px' 
        }}>
          <button 
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigateMonth('prev');
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: isDarkMode ? '#ffffff' : '#000000',
              fontSize: '18px',
              padding: '8px'
            }}
          >
            ‹
          </button>
          
          <h3 style={{ 
            margin: 0, 
            fontSize: '16px', 
            fontWeight: '600',
            color: isDarkMode ? '#ffffff' : '#000000'
          }}>
            {currentDate.format('MMMM YYYY')}
          </h3>
          
          <button 
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigateMonth('next');
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: isDarkMode ? '#ffffff' : '#000000',
              fontSize: '18px',
              padding: '8px'
            }}
          >
            ›
          </button>
        </div>

        {/* Weekday Headers */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(7, 1fr)', 
          gap: '4px',
          marginBottom: '8px'
        }}>
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} style={{ 
              textAlign: 'center', 
              fontSize: '12px', 
              fontWeight: '600',
              color: isDarkMode ? '#adb5bd' : '#6c757d',
              padding: '8px 4px'
            }}>
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(7, 1fr)', 
          gap: '4px' 
        }}>
          {days.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} style={{ height: '36px' }}></div>;
            }

            const isToday = date.isSame(today, 'day');
            const isSelected = selectedDate && date.isSame(selectedDate, 'day');
            const isDisabled = isDateDisabled(date);
            
            return (
              <button
                key={date.format('YYYY-MM-DD')}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Debug: Day clicked:', date.format('YYYY-MM-DD'));
                  if (!isDisabled) {
                    handleDateSelect(date);
                  }
                }}
                disabled={isDisabled}
                style={{
                  width: '36px',
                  height: '36px',
                  border: 'none',
                  borderRadius: '8px',
                  background: isSelected ? themeColor : 'transparent',
                  color: isSelected ? '#ffffff' : (isDarkMode ? '#ffffff' : '#000000'),
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: isToday ? '600' : '400',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: isDisabled ? 0.3 : 1,
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (!isDisabled && !isSelected) {
                    e.target.style.backgroundColor = isDarkMode ? '#404040' : '#f5f5f5';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDisabled && !isSelected) {
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {date.format('D')}
                {isToday && !isSelected && (
                  <div style={{
                    position: 'absolute',
                    bottom: '4px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '4px',
                    height: '4px',
                    borderRadius: '50%',
                    backgroundColor: themeColor
                  }}></div>
                )}
              </button>
            );
          })}
        </div>

        {/* Actions */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '8px', 
          marginTop: '16px',
          paddingTop: '12px',
          borderTop: `1px solid ${isDarkMode ? '#404040' : '#dee2e6'}`
        }}>
          <button 
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              background: 'transparent',
              color: isDarkMode ? '#adb5bd' : '#6c757d',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Cancel
          </button>
          {selectedDate && (
            <button 
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
              }}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: themeColor,
                color: '#ffffff',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              OK
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`mui-date-picker-container ${isDarkMode ? 'dark' : 'light'} ${className}`}
      style={{ '--theme-color': themeColor, position: 'relative', ...style }}
    >
      {label && (
        <label 
          className={`mui-label ${required ? 'required' : ''} ${error ? 'error' : ''}`}
          htmlFor={`debug-date-input-${name}`}
          style={{ 
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: '500',
            color: isDarkMode ? '#ffffff' : '#333333'
          }}
        >
          {label}
        </label>
      )}
      
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '10px',
        border: `1px solid ${isDarkMode ? '#404040' : '#D9D9D9'}`,
        backgroundColor: isDarkMode ? '#2d3436' : '#F9F9F9',
        height: '44px',
        transition: 'all 0.2s ease'
      }}>
        <input
          id={`debug-date-input-${name}`}
          type="text"
          value={selectedDate ? selectedDate.format('MM/DD/YYYY') : ''}
          placeholder={placeholder}
          onClick={toggleCalendar}
          readOnly
          disabled={disabled}
          name={name}
          style={{
            flex: 1,
            padding: '8px 12px',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: '14px',
            color: isDarkMode ? '#ffffff' : '#000000',
            cursor: 'pointer'
          }}
          {...props}
        />
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', paddingRight: '8px' }}>
          {clearable && selectedDate && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                border: 'none',
                borderRadius: '6px',
                background: 'transparent',
                color: isDarkMode ? '#adb5bd' : '#6c757d',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              <i className="fa fa-times"></i>
            </button>
          )}
          <button
            type="button"
            onClick={toggleCalendar}
            disabled={disabled}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              border: 'none',
              borderRadius: '6px',
              background: 'transparent',
              color: isDarkMode ? '#adb5bd' : '#6c757d',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            <i className="fa fa-calendar"></i>
          </button>
        </div>
      </div>

      {helperText && (
        <div style={{
          marginTop: '6px',
          fontSize: '12px',
          color: error ? '#d32f2f' : (isDarkMode ? '#adb5bd' : '#6c757d')
        }}>
          {helperText}
        </div>
      )}

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          zIndex: 9999,
          marginTop: '8px'
        }}>
          {renderSimpleCalendar()}
        </div>
      )}
    </div>
  );
};

export default DebugMUIDatePicker;
